export type CustomTooltipPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right';

export type TooltipDirection = 'top' | 'bottom' | 'left' | 'right';

type TooltipRectBounds = { left: number; right: number; top: number; bottom: number };

type TooltipCoordinates = { top: number; left: number };

type PositionCalculator = (params: {
  hostRect: DOMRect;
  tooltipRect: DOMRect;
  gap: number;
}) => TooltipCoordinates;

type TooltipGeometry = {
  hostRect: DOMRect;
  tooltipRect: DOMRect;
  viewportWidth: number;
  viewportHeight: number;
};

type TooltipPlacementParams = TooltipGeometry & {
  preferredPosition: CustomTooltipPosition;
  gap: number;
  padding: number;
};

type TooltipClampParams = {
  coords: TooltipCoordinates;
  tooltipRect: DOMRect;
  padding: number;
  viewportWidth: number;
  viewportHeight: number;
};

const positionFlipMap: Record<
  CustomTooltipPosition,
  {
    both?: CustomTooltipPosition;
    vertical?: CustomTooltipPosition;
    horizontal?: CustomTooltipPosition;
  }
> = {
  top: { vertical: 'bottom' },
  'top-left': { both: 'bottom-right', vertical: 'bottom-left', horizontal: 'top-right' },
  'top-right': { both: 'bottom-left', vertical: 'bottom-right', horizontal: 'top-left' },
  bottom: { vertical: 'top' },
  'bottom-left': { both: 'top-right', vertical: 'top-left', horizontal: 'bottom-right' },
  'bottom-right': { both: 'top-left', vertical: 'top-right', horizontal: 'bottom-left' },
  left: { horizontal: 'right' },
  right: { horizontal: 'left' },
};

const positionCalculators: Record<CustomTooltipPosition, PositionCalculator> = {
  top: ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.top - tooltipRect.height - gap,
    left: hostRect.left + (hostRect.width - tooltipRect.width) / 2,
  }),
  'top-left': ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.top - tooltipRect.height - gap,
    left: hostRect.left - tooltipRect.width - gap,
  }),
  'top-right': ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.top - tooltipRect.height - gap,
    left: hostRect.right + gap,
  }),
  bottom: ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.bottom + gap,
    left: hostRect.left + (hostRect.width - tooltipRect.width) / 2,
  }),
  'bottom-left': ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.bottom + gap,
    left: hostRect.left - tooltipRect.width - gap,
  }),
  'bottom-right': ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.bottom + gap,
    left: hostRect.right + gap,
  }),
  left: ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.top + (hostRect.height - tooltipRect.height) / 2,
    left: hostRect.left - tooltipRect.width - gap,
  }),
  right: ({ hostRect, tooltipRect, gap }) => ({
    top: hostRect.top + (hostRect.height - tooltipRect.height) / 2,
    left: hostRect.right + gap,
  }),
};

function getPositionCoordinates(
  position: CustomTooltipPosition,
  hostRect: DOMRect,
  tooltipRect: DOMRect,
  gap: number,
): TooltipCoordinates {
  return positionCalculators[position]({ hostRect, tooltipRect, gap });
}

function fitsVertically(
  top: number,
  height: number,
  padding: number,
  viewportHeight: number,
): boolean {
  return top >= padding && top + height <= viewportHeight - padding;
}

function fitsHorizontally(
  left: number,
  width: number,
  padding: number,
  viewportWidth: number,
): boolean {
  return left >= padding && left + width <= viewportWidth - padding;
}

function getFlippedPosition(
  position: CustomTooltipPosition,
  fitsInViewport: { vertically: boolean; horizontally: boolean },
): CustomTooltipPosition {
  const flipConfig = positionFlipMap[position];
  const flipType =
    !fitsInViewport.vertically && !fitsInViewport.horizontally
      ? 'both'
      : !fitsInViewport.vertically
        ? 'vertical'
        : 'horizontal';

  return flipConfig?.[flipType] ?? position;
}

export function calculateTooltipPlacement(params: TooltipPlacementParams): {
  position: CustomTooltipPosition;
  coords: TooltipCoordinates;
} {
  const originalCoords = getPositionCoordinates(
    params.preferredPosition,
    params.hostRect,
    params.tooltipRect,
    params.gap,
  );

  const fitsInViewport = {
    vertically: fitsVertically(
      originalCoords.top,
      params.tooltipRect.height,
      params.padding,
      params.viewportHeight,
    ),
    horizontally: fitsHorizontally(
      originalCoords.left,
      params.tooltipRect.width,
      params.padding,
      params.viewportWidth,
    ),
  };

  if (fitsInViewport.vertically && fitsInViewport.horizontally) {
    return { position: params.preferredPosition, coords: originalCoords };
  }

  const flippedPosition = getFlippedPosition(params.preferredPosition, fitsInViewport);

  if (flippedPosition === params.preferredPosition) {
    return { position: params.preferredPosition, coords: originalCoords };
  }

  return {
    position: flippedPosition,
    coords: getPositionCoordinates(
      flippedPosition,
      params.hostRect,
      params.tooltipRect,
      params.gap,
    ),
  };
}

export function clampTooltipToViewport(params: TooltipClampParams): TooltipCoordinates {
  return {
    left: Math.max(
      params.padding,
      Math.min(
        params.coords.left,
        params.viewportWidth - params.tooltipRect.width - params.padding,
      ),
    ),
    top: Math.max(
      params.padding,
      Math.min(
        params.coords.top,
        params.viewportHeight - params.tooltipRect.height - params.padding,
      ),
    ),
  };
}

function getTooltipDirection(position: CustomTooltipPosition): TooltipDirection {
  if (position.startsWith('top')) {
    return 'top';
  }

  if (position.startsWith('bottom')) {
    return 'bottom';
  }

  if (position === 'left') {
    return 'left';
  }

  return 'right';
}

export function calculateTooltipTransitZone(params: {
  hostRect: DOMRect;
  tooltipRect: DOMRect;
  position: CustomTooltipPosition;
  transitPadding: number;
  lateralTolerance: number;
}): TooltipRectBounds {
  const direction = getTooltipDirection(params.position);

  let zoneLeft = 0;
  let zoneRight = 0;
  let zoneTop = 0;
  let zoneBottom = 0;

  if (direction === 'top') {
    zoneLeft = Math.min(params.hostRect.left, params.tooltipRect.left) - params.lateralTolerance;
    zoneRight = Math.max(params.hostRect.right, params.tooltipRect.right) + params.lateralTolerance;
    zoneTop = params.tooltipRect.bottom - params.transitPadding;
    zoneBottom = params.hostRect.top + params.transitPadding;
  } else if (direction === 'bottom') {
    zoneLeft = Math.min(params.hostRect.left, params.tooltipRect.left) - params.lateralTolerance;
    zoneRight = Math.max(params.hostRect.right, params.tooltipRect.right) + params.lateralTolerance;
    zoneTop = params.hostRect.bottom - params.transitPadding;
    zoneBottom = params.tooltipRect.top + params.transitPadding;
  } else if (direction === 'left') {
    zoneLeft = params.tooltipRect.right - params.transitPadding;
    zoneRight = params.hostRect.left + params.transitPadding;
    zoneTop = Math.min(params.hostRect.top, params.tooltipRect.top) - params.lateralTolerance;
    zoneBottom =
      Math.max(params.hostRect.bottom, params.tooltipRect.bottom) + params.lateralTolerance;
  } else {
    zoneLeft = params.hostRect.right - params.transitPadding;
    zoneRight = params.tooltipRect.left + params.transitPadding;
    zoneTop = Math.min(params.hostRect.top, params.tooltipRect.top) - params.lateralTolerance;
    zoneBottom =
      Math.max(params.hostRect.bottom, params.tooltipRect.bottom) + params.lateralTolerance;
  }

  return {
    left: Math.min(zoneLeft, zoneRight),
    right: Math.max(zoneLeft, zoneRight),
    top: Math.min(zoneTop, zoneBottom),
    bottom: Math.max(zoneTop, zoneBottom),
  };
}

export function isPointInRect(point: { x: number; y: number }, rect: TooltipRectBounds): boolean {
  return (
    point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
  );
}
